/**
 * audit-timeline — turns the raw audit-history rows the two drift endpoints
 * return into the timeline the drawer's "Section · 03 · Audit trail" shows.
 *
 * Why this exists (2026-09-25 pre-demo sweep):
 *  1. Neither source writes a "Detected" audit row, so a freshly detected
 *     drift had NO trail at all and the section stayed hidden until someone
 *     acted on it. Detection is a real event with a real timestamp
 *     (`detectedAt` / `firstReportedDateTime`), so we seed the trail with it.
 *  2. A TCM drift the monitor stops reporting is flipped to Remediated by the
 *     ingestion cycle without an audit row (the emitter-driven closes DO log
 *     one). `remediatedAt` on the drift row is the only record, so we surface
 *     that as a synthetic Remediated entry when no real one exists.
 *  3. System-written TCM rows carry a machine actor (`…@system`) and the TCM
 *     audit endpoint doesn't return the display name, so the drawer printed
 *     the raw address. `friendlyActor` maps those to a Guardian byline.
 *  4. Every Accept or Apply re-baselines the monitor, and UTCM re-reports the
 *     settings still drifting as new rows. The server's `firstSeenDateTime`
 *     dates such a row from the occurrence it continues, so the trail gets a
 *     first-seen entry at that date below the re-report.
 *
 * Synthetic entries are derived from fields the drift row already carries;
 * nothing here invents a timestamp, an actor, or a decision.
 */

import type { DriftAuditHistoryEntry } from '@/lib/api/drift'
import type { TcmDriftAuditEntry, TcmDriftDetail } from '@/types/tcm'

export const SYNTHETIC_DETECTED_ID = 'synthetic-detected'
export const SYNTHETIC_REMEDIATED_ID = 'synthetic-remediated'
export const SYNTHETIC_FIRST_SEEN_ID = 'synthetic-first-seen'

/** Byline shown for a row's actor. Machine actors (`System` or a
 *  `…@system` address) are mapped to a curated Guardian name FIRST — the
 *  stored display name on those rows is often the raw jargon name
 *  ("Guardian snapshot-diff emitter") and must not win. Only once an actor
 *  isn't a recognized machine identity does a stored display name apply;
 *  otherwise the raw email is the fallback for humans. */
export function friendlyActor(triggeredBy: string | null | undefined, displayName?: string | null): string {
  const raw = (triggeredBy ?? '').trim()
  if (!raw || raw === 'System') return 'Guardian'
  const lower = raw.toLowerCase()
  if (lower.endsWith('@system')) {
    if (lower.startsWith('guardian-snapshot-diff-emitter')) return 'Guardian full-configuration capture'
    if (lower.startsWith('guardian-creation-detector')) return 'Guardian continuous watch'
    if (lower.startsWith('guardian-continuous-watch')) return 'Guardian continuous watch'
    return 'Guardian'
  }
  const dn = displayName?.trim()
  if (dn) return dn
  return raw
}

/** Shared event labels. "Approved" is the wire value; the product vocabulary
 *  for the user action is Accept (terminology-decisions-2026-09-22). */
export const AUDIT_EVENT_LABEL: Record<string, string> = {
  Detected: 'Detected',
  // Synthetic only (buildTcmTimeline): where a re-reported drift began.
  FirstSeen: 'First seen',
  Approved: 'Accepted',
  Rejected: 'Rejected',
  AutoApproved: 'Auto-accepted',
  Remediated: 'Remediated',
  Waived: 'Waived',
  ValuesEvolved: 'Values evolved',
  StatusChanged: 'Status changed',
  Redetected: 'Redetected',
}

export function auditEventLabel(eventType: string): string {
  return AUDIT_EVENT_LABEL[eventType] ?? eventType
}

type DscDriftForTimeline = { id: string; detectedAt: string }

/** DSC endpoint returns oldest-first; the drawer renders top-down in that
 *  order, so the Detected seed goes first. */
export function buildDscTimeline(
  drift: DscDriftForTimeline,
  entries: DriftAuditHistoryEntry[] | null | undefined,
): DriftAuditHistoryEntry[] {
  const real = entries ?? []
  const hasRealDetected = real.some(e => e.eventType === 'Detected')
  if (!drift.detectedAt || hasRealDetected) return real
  const detected: DriftAuditHistoryEntry = {
    id: SYNTHETIC_DETECTED_ID,
    driftId: drift.id,
    eventType: 'Detected',
    triggeredBy: 'Guardian',
    triggeredByObjectId: null,
    triggeredByDisplayName: 'Guardian',
    userRole: null,
    previousStatus: null,
    newStatus: 'New',
    remediationSource: null,
    comments: null,
    eventTimestamp: drift.detectedAt,
    additionalMetadata: null,
  }
  return [detected, ...real]
}

type TcmDriftForTimeline = Pick<TcmDriftDetail, 'firstReportedDateTime' | 'state' | 'remediatedAt'> & {
  source?: string | null
  firstSeenDateTime?: string | null
}

const REMEDIATION_MATCH_WINDOW_MS = 5 * 60 * 1000

/** TCM endpoint returns newest-first; the drawer renders top-down, so the
 *  Detected seed goes last and a synthetic Remediated (when the monitor
 *  closed the row without logging) goes first. */
export function buildTcmTimeline(
  drift: TcmDriftForTimeline,
  events: TcmDriftAuditEntry[] | null | undefined,
): TcmDriftAuditEntry[] {
  const real = [...(events ?? [])].sort(
    (a, b) => new Date(b.eventTimestamp).getTime() - new Date(a.eventTimestamp).getTime(),
  )
  const out: TcmDriftAuditEntry[] = []

  const isRemediated = (drift.state ?? '').toLowerCase() === 'remediated'
  if (isRemediated && drift.remediatedAt) {
    const closedAt = new Date(drift.remediatedAt).getTime()
    const alreadyLogged = real.some(e => {
      if (e.eventType === 'Remediated') return true
      if (e.eventType !== 'StatusChanged') return false
      return Math.abs(new Date(e.eventTimestamp).getTime() - closedAt) <= REMEDIATION_MATCH_WINDOW_MS
    })
    if (!alreadyLogged) {
      out.push({
        id: SYNTHETIC_REMEDIATED_ID,
        eventType: 'Remediated',
        sourceCollector: 'TCM',
        triggeredBy: 'Guardian',
        eventTimestamp: drift.remediatedAt,
        comments: 'The continuous watch no longer reports this change. The setting matches the baseline again.',
      })
    }
  }

  out.push(...real)

  const hasRealDetected = real.some(e => e.eventType === 'Detected')
  const isSweep = (drift.source ?? '').toLowerCase() === 'snapshotdiff'
  // A re-report after a baseline update: this row's own detection is the
  // re-report, and the setting has drifted since the earlier first-seen date.
  const firstSeen = isSweep ? null : drift.firstSeenDateTime ?? null
  if (drift.firstReportedDateTime && !hasRealDetected) {
    out.push({
      id: SYNTHETIC_DETECTED_ID,
      eventType: 'Detected',
      sourceCollector: 'TCM',
      triggeredBy: 'Guardian',
      eventTimestamp: drift.firstReportedDateTime,
      comments: isSweep
        ? 'First seen in a full-configuration capture.'
        : firstSeen
          ? 'Reported again by the continuous watch after a baseline update.'
          : 'First seen by the continuous watch.',
    })
  }
  if (firstSeen) {
    out.push({
      id: SYNTHETIC_FIRST_SEEN_ID,
      eventType: 'FirstSeen',
      sourceCollector: 'TCM',
      triggeredBy: 'Guardian',
      eventTimestamp: firstSeen,
      comments: 'It has drifted since. A baseline update reported it again later.',
    })
  }

  return out
}
